import { DEP_CARD_ID, DEP_PATH, QRY_LIMIT_PATH } from "csi/constants";
import ExtendedPath from "csi/core/ExtendedPath";
import imageBase from "csi/image/_base";
import locale from "csi/locale";
import SearchManager from "csi/page/SearchManager";
import reflection from "csi/reflection";
import { getSearch } from "csi/service/dataStoreService";
import StoreNodesConstructor, { StoreNodes } from "csi/store/Nodes";
import { C8Node, ICache } from "csi/typings";
import { ViewWidget } from "csi/view/Widget";
import xml from "csi/xml";
import Query from "csi/xml/Query";
import queryProcessor from "csi/xml/QueryProcessor";
import Search from "csi/xml/Search";
import Deferred from "csi/_base/Deferred";
import config from "dojo/_base/config";
import declare from "dojo/_base/declare";
import lang from "dojo/_base/lang";
import _ from "underscore";

const FOOD = "Food";
const PRODUCT_PRESENTATION = "ProductPresentation";
const PRODUCT_PRESENTATION_SET = "ProductPresentationSet";
const PRODUCT_PRESENTATION_LAYOUT = "ProductPresentationLayout";
const PRINT_DESIGN_COLOR = "PrintDesignColor";
const MAIN_MATERIAL = "MainMaterial";
const MAIN_MATERIALS = "MainMaterials";
const MATERIAL = "Material";
const STYLE = "Style";
const COLORWAY = "Colorway";
const COLORMATERIAL = "ColorMaterial";
const COLOR_SPECIFICATION = "ColorSpecification";
const PRODUCT_COLOR_SPECIFICATION = "ProductColorSpec";
const BASE_PRODUCT = "BaseProduct";
const THEME = "Theme";
const THEME_MASTER = "ThemeMaster";

// Product Presentation Types Map
const PP_TYPES_MAP: Record<string, string[]> = {
	__None__Style: ["ThemeMaster", "Theme", "ProductPresentationSet", "Style", "ProductColorSpec", "Material"],
	__None__Material: ["ProductPresentationSet", "Material", "ProductColorSpec"],
	__None__Food: ["ProductPresentationSet", "Food"],
	ColorSpecificationMaterial: ["ColorMaterial"],
	ColorSpecificationStyle: ["Colorway"],
	Food: ["ProductPresentationSet", "Images"],
	MainMaterial: ["Style"],
	Material: ["ProductPresentationSet", "ColorMaterial", "ColorSpecification", "Images"],
	ProductPresentationSetStyle: ["ProductPresentationSetItem", "Colorway", "ColorSpecification", "Material", "ThemeMaster", "Theme", "Images"],
	ProductPresentationSetMaterial: ["ProductPresentationSetItem", "ColorSpecification", "Images"],
	ProductPresentationSetFood: ["ProductPresentationSetItem", "Images"],
	Style: ["ThemeMaster", "Theme", "ProductPresentationSet", "Colorway", "ColorSpecification", "Material", "Images"],
	Theme: ["ThemeMaster", "ProductPresentationSet", "Style", "Images"],
	ThemeMaster: ["Theme", "ProductPresentationSet", "Style"],
};

/** Placeholder Path Types */
type PlaceholderPath = {
	sourceType: string;
	targetType: string;
	path: string;
	pathFunction?: Function;
	titleFunction?: Function;
	filterQuery?: Query;
}
type PlaceholderPathTargetMap = Record<string, PlaceholderPath>;
type PlaceholderPathMap = Record<string, PlaceholderPathTargetMap>;

/** The map of node types to list of paths and/or queries used to get node instances */
const _placeholderPathData: PlaceholderPathMap = {};

type ReferenceDataTypeFunction = (node: C8Node, cache: ICache) => boolean | undefined;
type ReferenceDataTitleFunction = (node: C8Node, cache: ICache) => string | undefined;
type ReferenceData = {
	sourceType: string;
	targetType: string;
	typeFunction: ReferenceDataTypeFunction;
	titleFunction: ReferenceDataTitleFunction;
}
type ReferenceDataTargetMap = Record<string, ReferenceData>;
type ReferenceDataMap = Record<string, ReferenceDataTargetMap>;

/** The same as placeholder path data, but for reference data. */
const _referenceData: ReferenceDataMap = {};

// remove duplicates, get color specifications from colorway nodes
const ColorSpecStore = declare(StoreNodesConstructor, {
	cachingStore: undefined,
	colorQuery: undefined,
	filterQuery: undefined,
	productColorSearch: undefined,

	/** the query object from search textbox */
	textBoxQuery: undefined,

	_resolveData: async function (dfd) {
		const { cachingStore, colorQuery, productColorSearch, filterQuery } = this;
		productColorSearch.addDependencyPaths(["Child:ColorSpecification"]);
		productColorSearch.getQuery().importQuery(colorQuery);
		const colorSpecNodes = [];
		try {
			const response = await productColorSearch.execute();
			cachingStore.cacheResponse(response);
			const responseNodes = response.getResultNodes();
			responseNodes.forEach((node) => {
				const colorNode = cachingStore.getNode(node.ColorSpecification);
				if (colorNode) {
					colorSpecNodes.push(colorNode);
				}
			});
			const uniqueNodes = _.uniq(colorSpecNodes, xml.nodeGetURL);

			// apply the query against the slim card
			const query = new Query();

			if (this.textBoxQuery) {
				this._getFilterQueryFromQuery(this.textBoxQuery, query);
			}

			// include the query generated by the attribute(multiple select widget)
			query.importQuery(filterQuery);

			// filter the color specification nodes based on the overall filter
			const finalNodes = query ? queryProcessor.applyQueryToNodes(query, uniqueNodes, cachingStore) : uniqueNodes;

			dfd.resolve(xml.sortNodes(finalNodes));
		} catch (err) {
			dfd.reject(err);
		}
	},
	/** have to override this, so that would always issue an query  */
	_createQueryRequest: function (query, options) {
		// summary: generate Deferred that will be resolved when store is complete -
		// force it to be asynchronous to work with csi/store/Request "query" method so that
		// it is not resolved before _dfdQueries is populated

		var dfd = new Deferred();
		this.textBoxQuery = query;

		_.defer(lang.hitch(this, this._resolveData, dfd));
		return dfd;
	},
	/** need to specify the url(s) as the limit path to get the color specification nodes */
	getValuesStore(spec, storeOptions?: object) {
		const store = this.inherited<StoreNodes>(arguments);
		store.requestParameters ||= {};
		store.requestParameters[QRY_LIMIT_PATH] = this.resultUrls;

		return store;
	},
});

/** Given a node and cache, determine product type of presentation or layout */
export function getPresentationProductType(node, cache): string {
	let url;
	if (node?.$Type !== PRODUCT_PRESENTATION) {
		url = node.__Parent__;
	} else {
		url = node.$URL;
	}
	const presentation = cache.get(url);
	return xml.enumGetText(presentation.ProductType);
}


/** Add a reference data type */
export function addReferenceDataType(
	sourceType: string,
	targetType: string,
	typeFunction?: ReferenceDataTypeFunction,
	titleFunction?: ReferenceDataTitleFunction,
): void {
	_referenceData[sourceType] ||= {};
	_referenceData[sourceType][targetType] = {
		sourceType,
		targetType,
		typeFunction,
		titleFunction,
	};
}

/** Return true if the reference data type is accessible */
export function canGetReferenceDataType(sourceType: string, targetType: string): boolean {
	// for now, reference data only exists in pp layout types
	if (sourceType !== PRODUCT_PRESENTATION_LAYOUT) {
		return false;
	}
	const possibleTypes = [];
	Object.keys(_referenceData[sourceType]).forEach((rootType) => {
		// push root type and its children to the total selectable types
		possibleTypes.push(rootType);
		Object.keys(_placeholderPathData[rootType]).forEach((type) => {
			possibleTypes.push(type);
		});
	});
	return possibleTypes.includes(targetType);
}

/** Get reference data store by type */
export function getReferenceDataTypeStore(sourceType: string, targetType: string, url: string, cache: ICache): StoreNodes | undefined {
	const referenceDataTarget = _referenceData[sourceType]?.[targetType];
	if (referenceDataTarget) {
		const { typeFunction, titleFunction } = referenceDataTarget;
		const node = cache.get(url);
		if (typeFunction && !typeFunction(node, cache)) {
			return;
		}
		const search = new Search().byType(targetType);
		SearchManager.getSearchTypeQuery(targetType, search.getQuery());
		const pathTitle = titleFunction && titleFunction(node, cache) || locale.typePlural(targetType);
		return new StoreNodesConstructor({
			cachingStore: cache,
			filterQuery: search.getQuery(),
			pathTitle,
			sortQuery: new Query().SortByAttr(xml.ATTR_NAME),
			typeNode: targetType,
		});
	}
}

/** Get reference data store by type */
export function getReferenceDataTypes(sourceType: string): string[] {
	return _.keys(_referenceData[sourceType]);
}

/** Remove a reference data type */
export function removeReferenceDataType(sourceType: string, targetType?: string): void {
	if (targetType) {
		delete _referenceData[sourceType]?.[targetType];
	} else if (_referenceData[sourceType]) {
		delete _referenceData[sourceType];
	}
}

/**
 * Add to the map of paths, for given path information.
 * To add multiple paths, call once per path.
 */
export function add(
	sourceType: string, // type used to begin the search for paths
	targetType: string, // type to refine the search for paths
	path: string, // a single path used to perform the query; this can be an extended (typed) path
	pathFunction?: Function, // function receiving the node, to determine path returned
	filterQuery?: Query, // search query
	titleFunction?: Function, // adds a title function to identify path later
): void {
	// Validate path
	const rcSource = reflection.getClass(sourceType);

	if (config.isDebug && path && !ExtendedPath.create(rcSource, path)) {
		return;
	}

	const pathData = {
		sourceType,
		targetType,
		path,
		pathFunction,
		filterQuery,
		titleFunction,
	};

	const sourceTargetString = sourceType + "." + targetType;
	lang.setObject(sourceTargetString, pathData, _placeholderPathData);
}

/**
 * Return whether a path to the nodes can be found
 */
export function canGet(sourceType: string, targetType: string, url: string, cache: ICache): boolean {
	const node = cache.get(url);

	if (node) {
		const source = _placeholderPathData[sourceType];

		if (!source) {
			return false;
		}

		let target = source[targetType];

		if (!target) {
			// check if target is derived
			const rcTarget = reflection.getClass(targetType);
			const typesOfSource = Object.keys(source);
			typesOfSource.some((type) => {
				const isTargetDerivedFromType = rcTarget.isDerivedFrom(type);
				if (isTargetDerivedFromType) {
					target = source[type];
				}
				return isTargetDerivedFromType;
			});
		}

		if (!target) {
			return false;
		}

		const pathFunction = target.pathFunction;
		let { path } = target;

		if (pathFunction) {
			path = pathFunction(node, cache);

			if (path === null) {
				return false;
			}

			// Validate path
			const rcSource = reflection.getClass(sourceType);

			if (path && !ExtendedPath.create(rcSource, path)) {
				return false;
			}
		}

		if (path) {
			return true;
		}
	}
	return false;
}

/**
 * Return a store containing node instances for the types, possibly filtered.
 */
export function get(
	sourceType: string, // type used to begin the search
	targetType: string, // type to refine the search
	url: string, // to the node
	cache: ICache, // application cache to get nodes
): StoreNodes {
	const node = cache.get(url);

	if (node) {
		const source = _placeholderPathData[sourceType];
		const target = source?.[targetType];

		if (!target) {
			return null;
		}

		const { filterQuery, pathFunction, titleFunction } = target;
		let path = target.path;

		if (pathFunction) {
			path = pathFunction(node, cache);

			if (path === null) {
				return null;
			}

			// Validate path
			const rcSource = reflection.getClass(sourceType);

			if (path && !ExtendedPath.create(rcSource, path)) {
				return;
			}
		}

		const pathTitle = titleFunction?.(node, cache) || locale.typePlural(targetType);

		if (path) {
			const queryPath = xml.getUrlPath(url, path);

			// Add cache store option, sort option, and the type of node for request.
			const options = {
				cachingStore: cache,
				sortQuery: new Query().SortByAttr(xml.ATTR_NAME),
				typeNode: targetType,
				filterQuery,
				requestParameters: {
					// Limit the request to the supplied paths
					[DEP_CARD_ID]: "Slim",
					[QRY_LIMIT_PATH]: queryPath,
				},
				pathTitle,
			};

			return new StoreNodesConstructor(options);
		}
	}
}

/**
 * Remove data for a sourceType and targetType. Remove all data for sourceType if targetType not specified.
 */
export function remove(sourceType: string, targetType: string): void {
	if (sourceType && targetType) {
		delete _placeholderPathData[sourceType][targetType];
	} else if (sourceType) {
		delete _placeholderPathData[sourceType];
	}
}

/**
 * Filter for valid types.
 */
export function _filterTypes(types: string[]): string[] {
	return _.filter(types, (type) => reflection.getClass(type)?.enabled);
}

/**
 * Return target types for provided source type.
 */
export function getTargetTypes(sourceType: string): string[] {
	let types = _.keys(_placeholderPathData[sourceType]);
	types = _filterTypes(types);
	return types;
}

/**
 * Return title for type based on source type
 * @param sourceType
 * @param targetType
 * @return
 */
export function getTitleForType(sourceType: string, targetType: string): string {
	if (!sourceType || !targetType) {
		return locale.type(targetType);
	}
	const titleFunc = _placeholderPathData[sourceType][targetType]?.titleFunction;
	return titleFunc?.() ?? locale.type(targetType);
}

/**
 * Return all target types.
 */
export function getAllTargetTypes(): string[] {
	const _types = (pathData: PlaceholderPathMap): string[] => {
		const keys = _.keys(pathData);
		return keys.reduce((typesArray, key) => {
			return typesArray.concat(_.keys(pathData[key]));
		}, []);
	};
	const types = _filterTypes(_types(_placeholderPathData));
	return _.uniq(_.flatten(_.values(types)));
}

/**
 * Get a string array of placeholder types for canvasType/presentationType/selectedNode
 */
export function getPlaceholderTypes(canvasType: string, presentationType: string, selectedNode: C8Node): string[] {
	if (!selectedNode) {
		if (canvasType === PRODUCT_PRESENTATION_LAYOUT) {
			return PP_TYPES_MAP["__None__" + presentationType];
		} else if (canvasType) {
			const rc = reflection.getClass(canvasType);
			const type = rc.isMaster() ? rc.getRevisionClass().getType() : canvasType;
			return getTargetTypes(type);
		} else {
			return getAllTargetTypes();
		}
	} else {
		const selectedType = selectedNode.$Type;
		if (presentationType === STYLE && selectedType === MATERIAL) {
			return PP_TYPES_MAP[MAIN_MATERIAL];
		} else if (selectedType === COLOR_SPECIFICATION || selectedType === PRINT_DESIGN_COLOR) {
			return PP_TYPES_MAP[COLOR_SPECIFICATION + presentationType];
		} else if (selectedType === PRODUCT_PRESENTATION_SET) {
			return PP_TYPES_MAP[PRODUCT_PRESENTATION_SET + presentationType];
		} else {
			return PP_TYPES_MAP[selectedType];
		}
	}
}

/**
 * Get search from data stores for related types.
 */
export function queryMaker(
	canvasType: string,
	selectedNode: C8Node,
	placeholderType: string,
	presentationType: string,
	view: ViewWidget,
	colorQuery: Query,
): StoreNodes {
	if (canvasType !== PRODUCT_PRESENTATION_LAYOUT) {
		return get(canvasType, placeholderType, view.urlRoot, view.cacheView);
	}
	const { cacheView } = view;
	const presentationLayout = view.getViewNode();
	const presentationUrl = presentationLayout.__Parent__;
	const _isProduct = (product): boolean => [STYLE, MATERIAL, FOOD].includes(product);
	const placeholderProduct = _isProduct(placeholderType);
	const pathTitle = locale.typePlural(placeholderType);

	let search;
	let useStore;
	if (selectedNode) {
		const [selectedUrl, selectedType] = [selectedNode.$URL, selectedNode.$Type];
		const selectedProduct = _isProduct(selectedType);
		if (placeholderType === MATERIAL && selectedType === PRODUCT_PRESENTATION_SET) {
			// Data Store: Material.ProductPresentationSet.MainMaterials
			search = getSearch(`${MATERIAL}.${PRODUCT_PRESENTATION_SET}.${MAIN_MATERIALS}`, selectedUrl);
		} else if (placeholderType === COLOR_SPECIFICATION && (selectedType === STYLE || selectedType === MATERIAL)) {
			const useType = selectedType === STYLE ? COLORWAY : COLORMATERIAL;
			useStore = new ColorSpecStore({
				colorQuery,
				cachingStore: cacheView,
				pathTitle,
				// Data Stores: Colorway.Style | ColoredMaterial.Material
				productColorSearch: getSearch(`${useType}.${selectedType}`, selectedUrl),
			});
		} else if (placeholderType === COLOR_SPECIFICATION && selectedType === PRODUCT_PRESENTATION_SET) {
			// Data Stores: ColorSpecification.ProductPresentationSet
			search = getSearch(`${COLOR_SPECIFICATION}.${PRODUCT_PRESENTATION_SET}`, selectedUrl, { presentationUrl });
		} else if (placeholderType === reflection.ATTR_IMAGES) {
			useStore = imageBase.getNodeImageStore(selectedNode);
		} else if (selectedType === MATERIAL && presentationType === STYLE) {
			// Data Store: Style.Material.MainMaterials
			search = getSearch(`${STYLE}.${MATERIAL}.${MAIN_MATERIALS}`, selectedUrl, { presentationUrl });
		} else if (placeholderType === STYLE && selectedType === THEME) {
			// Data Store: Style.Theme
			search = getSearch(`${STYLE}.${THEME}`, selectedUrl, { presentationUrl });
		} else if (placeholderType === THEME && selectedType === STYLE) {
			// Data Store: Theme.Style
			search = getSearch(`${THEME}.${STYLE}`, selectedUrl, { presentationUrl });
		} else if (placeholderType === THEME_MASTER && selectedType === STYLE) {
			// Data Store: ThemeMaster.Style
			search = getSearch(`${THEME_MASTER}.${STYLE}`, selectedUrl, { presentationUrl });
		} else if (placeholderType === STYLE && selectedType === THEME_MASTER) {
			// Data Store: Style.ThemeMaster
			search = getSearch(`${STYLE}.${THEME_MASTER}`, selectedUrl, { presentationUrl });
		} else if (placeholderType === THEME_MASTER && selectedType === THEME) {
			// Data Store: ThemeMaster.Theme
			search = getSearch(`${THEME_MASTER}.${THEME}`, selectedUrl, { presentationUrl });
		} else if (placeholderType === MATERIAL && selectedProduct) {
			// Data Store: Material.BaseProduct
			search = getSearch(`${MATERIAL}.${BASE_PRODUCT}`, selectedUrl, { presentationUrl });
		} else if (placeholderProduct) {
			// Data Store: BaseProduct.ProductPresentationSet
			search = getSearch(`${BASE_PRODUCT}.${selectedType}`, selectedUrl, { type: placeholderType });
		} else if (selectedProduct) {
			// Data Stores:
			// ColorMaterial.BaseProduct
			// Colorway.BaseProduct
			// ProductPresentationSet.BaseProduct
			search = getSearch(`${placeholderType}.${BASE_PRODUCT}`, selectedUrl, { presentationUrl });
			if (placeholderType !== PRODUCT_PRESENTATION_SET) {
				search.getQuery().importQuery(colorQuery);
			}
		} else {
			// Data Stores:
			// ColorMaterial.ColorSpecification
			// Colorway.ColorSpecification
			// ProductPresentationSetItem.ProductPresentationSet
			// ThemeMaster.ProductPresentationSet
			// Theme.ProductPresentationSet
			// Theme.ThemeMaster
			// ProductPresentationSet.ThemeMaster
			search = getSearch(`${placeholderType}.${selectedType}`, selectedUrl, { presentationUrl });
		}
	} else if (placeholderType === PRODUCT_COLOR_SPECIFICATION) {
		useStore = new ColorSpecStore({
			colorQuery,
			cachingStore: cacheView,
			pathTitle,
			// Data Store: ProductColor.ProductPresentation
			productColorSearch: getSearch("ProductColor.ProductPresentation", presentationUrl),
		});
	} else if (placeholderType === MATERIAL && presentationType === STYLE) {
		// Data Store: Material.ProductPresentation.MainMaterials
		search = getSearch(`${MATERIAL}.${PRODUCT_PRESENTATION}.${MAIN_MATERIALS}`, presentationUrl);
	} else if (placeholderProduct) {
		// Data Store: BaseProduct.ProductPresentation
		search = getSearch(`${BASE_PRODUCT}.${PRODUCT_PRESENTATION}`, presentationUrl, { type: placeholderType });
	} else if (placeholderType === PRODUCT_PRESENTATION_SET) {
		// Data Store: ProductPresentationSet.ProductPresentation
		search = getSearch(`${PRODUCT_PRESENTATION_SET}.${PRODUCT_PRESENTATION}`, presentationUrl);
	} else if (placeholderType === THEME) {
		// Data Store: Theme.ProductPresentation
		search = getSearch(`${THEME}.${PRODUCT_PRESENTATION}`, presentationUrl);
	} else if (placeholderType === THEME_MASTER) {
		// Data Store: ThemeMaster.ProductPresentation
		search = getSearch(`${THEME_MASTER}.${PRODUCT_PRESENTATION}`, presentationUrl);
	}

	if (!useStore) {
		useStore = new StoreNodesConstructor({
			cachingStore: cacheView,
			filterQuery: search.getQuery(),
			pathTitle,
			requestParameters: {
				[DEP_PATH]: search.getParameter(DEP_PATH),
				[QRY_LIMIT_PATH]: search.getParameter(QRY_LIMIT_PATH),
			},
			sortQuery: new Query().SortByAttr(xml.ATTR_NAME),
			typeNode: placeholderType,
		});
	}

	return useStore;
}